import { supabase, supabaseWithoutCSRF } from "../supabaseClient";
import { saveSignupSheetImport } from "./signupSheetImports";
import {
  ALLOWED_LEAGUE_SHEET_TYPES,
  LEAGUE_SHEET_BUCKET,
  MAX_STORED_FILE_BYTES,
  chooseLeagueSheetFiles,
  sanitizeLeagueSheetFileName,
} from "./averageCatalogs";

const RAW_CODE_ERROR =
  /\bMistral\b|\bsupabase\b|\bis not defined\b|\bis not a function\b|\bcannot read propert|\bcannot set propert|\bunexpected token\b|\breferenceerror\b|\btypeerror\b|\bsyntaxerror\b|\bshowHBrk\b|\bshowSBrk\b/i;

export const SIGNUP_EXTRACT_ERROR =
  "Could not extract this signup sheet. Check the photo and try again.";

export const signupExtractErrorMessage = (
  error,
  fallback = SIGNUP_EXTRACT_ERROR
) => {
  const message =
    typeof error === "string"
      ? error.trim()
      : String(error?.message || "").trim();
  if (!message || RAW_CODE_ERROR.test(message)) return fallback;
  return message;
};

export const mimeTypeForSignupSheet = (file) => {
  if (ALLOWED_LEAGUE_SHEET_TYPES.includes(file?.type)) return file.type;
  const name = String(file?.name || "").toLowerCase();
  if (name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  return file?.type || "";
};

const bakeImageOrientation = async (file) => {
  const mimeType = mimeTypeForSignupSheet(file);
  if (!mimeType.startsWith("image/") || typeof createImageBitmap !== "function") {
    return file;
  }
  try {
    const bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    });
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0);
    bitmap.close();
    const blob = await new Promise((resolve) =>
      canvas.toBlob(
        resolve,
        mimeType === "image/png" ? "image/png" : "image/jpeg",
        0.92
      )
    );
    if (!blob) return file;
    return new File([blob], file.name, {
      type: blob.type || mimeType,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
};

export const chooseSignupSheetFiles = (fileList) => {
  const normalized = Array.from(fileList || []).map((file) => {
    const mimeType = mimeTypeForSignupSheet(file);
    if (!mimeType || mimeType === file.type) return file;
    return new File([file], file.name, {
      type: mimeType,
      lastModified: file.lastModified,
    });
  });
  return chooseLeagueSheetFiles(normalized);
};

export const namedSignupEntries = (extraction, { teamSize } = {}) => {
  const named = (extraction?.entries || [])
    .map((entry) => ({
      ...entry,
      bowlers: (entry.bowlers || []).filter((bowler) =>
        String(bowler?.name || "").trim()
      ),
    }))
    .filter((entry) => entry.bowlers.length);
  return packSignupSheetEntries(named, teamSize);
};

const normalizeTeamName = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

export const packSignupSheetEntries = (entries, teamSize) => {
  const size = Math.max(1, Number(teamSize) || 1);
  if (size <= 1) return entries || [];
  const packed = [];
  let buffer = null;

  const flush = () => {
    if (!buffer) return;
    packed.push(buffer);
    buffer = null;
  };

  (entries || []).forEach((entry) => {
    let remaining = (entry?.bowlers || []).filter((bowler) =>
      String(bowler?.name || "").trim()
    );
    if (!remaining.length) return;

    while (remaining.length) {
      if (buffer && buffer.bowlers.length >= size) flush();
      if (!buffer) {
        const take = remaining.slice(0, size);
        buffer = { ...entry, bowlers: take };
        remaining = remaining.slice(take.length);
        continue;
      }
      const sameName =
        Boolean(normalizeTeamName(buffer.teamName)) &&
        normalizeTeamName(entry.teamName) === normalizeTeamName(buffer.teamName);
      const untitledFollowOn = !normalizeTeamName(entry.teamName);
      if (!sameName && !untitledFollowOn) {
        flush();
        continue;
      }
      const take = remaining.slice(0, size - buffer.bowlers.length);
      if (!buffer.teamName && entry.teamName) buffer.teamName = entry.teamName;
      if (!buffer.lane && entry.lane) buffer.lane = entry.lane;
      buffer.bowlers = buffer.bowlers.concat(take);
      remaining = remaining.slice(take.length);
    }
  });
  flush();
  return packed;
};

export const uploadSignupSheetFile = async ({
  organizationID,
  tournamentId,
  batchId,
  file,
}) => {
  const fileName = sanitizeLeagueSheetFileName(file.name);
  const storagePath = `${organizationID}/signup-sheets/${tournamentId}/${batchId}/${fileName}`;
  const mimeType = mimeTypeForSignupSheet(file) || file.type;
  const { error } = await supabase.storage
    .from(LEAGUE_SHEET_BUCKET)
    .upload(storagePath, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: mimeType,
    });
  if (error) throw error;
  return {
    storagePath,
    fileName,
    mimeType,
  };
};

export const processSignupSheetFile = async ({
  tournamentId,
  squadId,
  storagePath,
  fileName,
  mimeType,
}) => {
  const {
    data: { session },
  } = await supabaseWithoutCSRF.auth.getSession();
  if (!session?.access_token) {
    throw new Error("You need to be signed in.");
  }

  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const response = await fetch(
    `${supabaseUrl}/functions/v1/extract-signup-sheets`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: supabaseAnonKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        tournamentId,
        squadId,
        storagePath,
        fileName,
        mimeType,
      }),
    }
  );

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    throw new Error(signupExtractErrorMessage(error));
  }
  if (!response.ok) {
    throw new Error(signupExtractErrorMessage(payload?.error));
  }
  return payload;
};

const loadSignupReviewEvents = async (tournamentId) => {
  if (!tournamentId) return [];
  const { data, error } = await supabase
    .from("tournamentEvents")
    .select(
      "id, eventName, eventAbbreviation, isPrimary, bowlerQuantity, templateId, squadId"
    )
    .eq("tournamentID", tournamentId)
    .eq("isDeleted", false);
  if (error) {
    console.error(error);
    return [];
  }
  return data || [];
};

export const extractSignupSheetFiles = async ({
  organizationID,
  tournamentId,
  squadId,
  files,
  onProgress,
  prepareSheet,
}) => {
  const queue = [...(files || [])];
  const total = queue.length;
  const failures = [];
  const sheets = [];
  const savedSheets = [];
  if (!organizationID) {
    throw new Error("Choose an organization.");
  }
  if (!squadId) {
    throw new Error("Choose a squad.");
  }
  if (total === 0) {
    return { sheets, savedSheets, failures };
  }

  const {
    data: { session },
  } = await supabaseWithoutCSRF.auth.getSession();
  const createdBy = session?.user?.id || null;
  const batchId = crypto.randomUUID();
  const reviewEvents = await loadSignupReviewEvents(tournamentId);
  let done = 0;
  for (const file of queue) {
    try {
      const uploaded = await uploadSignupSheetFile({
        organizationID,
        tournamentId,
        batchId,
        file: await bakeImageOrientation(file),
      });
      const payload = await processSignupSheetFile({
        tournamentId,
        squadId,
        ...uploaded,
      });
      let sheet = { ...payload, consumedKeys: [] };
      if (prepareSheet) {
        sheet = prepareSheet(sheet, reviewEvents) || sheet;
      }
      sheets.push(sheet);
      if (
        !namedSignupEntries(sheet.extraction, { teamSize: sheet.teamSize })
          .length
      ) {
        continue;
      }
      try {
        const saved = await saveSignupSheetImport({
          ...sheet,
          organizationID,
          createdBy,
        });
        savedSheets.push({
          ...sheet,
          importId: saved.importId,
          consumedKeys: saved.consumedKeys || [],
          organizationID: saved.organizationID,
          createdAt: saved.createdAt,
          status: saved.status,
        });
      } catch (error) {
        console.error(error);
        savedSheets.push(sheet);
        failures.push(
          `${file.name}: Extracted on this device. Could not save for review on another device.`
        );
      }
    } catch (error) {
      console.error(error);
      failures.push(`${file.name}: ${signupExtractErrorMessage(error)}`);
    } finally {
      done += 1;
      onProgress?.({ done: Math.min(done, total), total });
    }
  }

  return { sheets, savedSheets, failures };
};

export const MAX_SIGNUP_SHEET_BYTES = MAX_STORED_FILE_BYTES;
